import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { UserPlus, Copy, Check, X, Loader2, Plus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { NotionButton } from '../ui/notion';
import { createInvite, InviteApiError, type InviteErrorCode } from '../../lib/services/invitesApi';

export interface InviteRole {
  id: string;
  name: string;
}

interface InviteRow {
  key: number;
  email: string;
  roleId: string;
  status: 'idle' | 'sending' | 'success' | 'error';
  errorMessage?: string;
  inviteUrl?: string;
  copied?: boolean;
}

export interface InviteRowsFormProps {
  roles: InviteRole[];
  /** Chiamato una volta se almeno un invito riesce. */
  onAnySuccess?: () => void;
  submitLabel?: string;
  /** Nasconde il bottone interno: il parent invia via ref (es. CTA unica in TeamInvitePage). */
  hideSubmit?: boolean;
  /** Notifica il parent quando ci sono email da inviare (per adattare la CTA esterna). */
  onHasEmailsChange?: (hasEmails: boolean) => void;
}

export interface InviteRowsFormHandle {
  /** Invia gli inviti delle righe compilate. Ritorna quanti riusciti/falliti. */
  submit: () => Promise<{ sent: number; failed: number }>;
}

const ERROR_MESSAGES: Record<InviteErrorCode, string> = {
  already_registered: 'Questa email ha già un account Flusia',
  already_invited: 'Già invitata',
  seats_full: 'Posti del piano esauriti',
  invalid_role: 'Ruolo non valido',
  invalid_email: 'Email non valida',
  invalid_invite: 'Invito non valido',
  terms_required: 'Termini richiesti',
  invalid_name: 'Nome non valido',
  weak_password: 'Password troppo debole',
  email_mismatch: 'Email non corrispondente',
  unknown: 'Errore, riprova',
};

let rowKeySeq = 0;
const newRow = (roleId: string): InviteRow => ({
  key: ++rowKeySeq,
  email: '',
  roleId,
  status: 'idle',
});

/**
 * Form righe email+ruolo per invitare membri del team, con submit integrato.
 * Riusato sia dal modale Impostazioni (InviteTeamModal) sia dallo step
 * "Invita team" della registrazione (TeamInvitePage): niente dipendenza da
 * useApp()/toast, l'errore "Inserisci almeno un'email" è mostrato inline.
 */
export const InviteRowsForm = forwardRef<InviteRowsFormHandle, InviteRowsFormProps>(function InviteRowsForm(
  { roles, onAnySuccess, submitLabel = 'Invia inviti', hideSubmit = false, onHasEmailsChange },
  ref,
) {
  const defaultRoleId = roles[0]?.id || '';
  const [rows, setRows] = useState<InviteRow[]>(() => [newRow(defaultRoleId)]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Segnala al parent se ci sono righe ancora da inviare (email compilata, non già riuscita).
  const hasEmails = rows.some(r => r.email.trim() && r.status !== 'success');
  useEffect(() => {
    onHasEmailsChange?.(hasEmails);
  }, [hasEmails, onHasEmailsChange]);

  const updateRow = (key: number, patch: Partial<InviteRow>) => {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows(prev => [...prev, newRow(defaultRoleId)]);

  const removeRow = (key: number) => setRows(prev => (prev.length > 1 ? prev.filter(r => r.key !== key) : prev));

  const copyLink = async (key: number, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      updateRow(key, { copied: true });
      setTimeout(() => updateRow(key, { copied: false }), 2000);
    } catch {
      setFormError('Impossibile copiare il link');
    }
  };

  const handleSubmit = async (): Promise<{ sent: number; failed: number }> => {
    setFormError(null);
    const validRows = rows.filter(r => r.email.trim() && r.status !== 'success');
    if (validRows.length === 0) {
      setFormError('Inserisci almeno un\'email');
      return { sent: 0, failed: 0 };
    }

    setSubmitting(true);
    let sent = 0;
    let failed = 0;

    for (const row of validRows) {
      updateRow(row.key, { status: 'sending', errorMessage: undefined });
      try {
        const { invite_url } = await createInvite(row.email.trim(), row.roleId);
        updateRow(row.key, { status: 'success', inviteUrl: invite_url });
        sent += 1;
      } catch (err) {
        const code = err instanceof InviteApiError ? err.code : 'unknown';
        updateRow(row.key, { status: 'error', errorMessage: ERROR_MESSAGES[code] || ERROR_MESSAGES.unknown });
        failed += 1;
      }
    }

    setSubmitting(false);
    if (sent > 0) onAnySuccess?.();
    return { sent, failed };
  };

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  return (
    <div className="space-y-3">
      {rows.map(row => (
        <div key={row.key} className="space-y-1.5">
          <div className="flex items-center gap-2">
            <input
              type="email"
              value={row.email}
              onChange={e => updateRow(row.key, { email: e.target.value, status: 'idle', errorMessage: undefined })}
              placeholder="email@esempio.it"
              disabled={row.status === 'sending' || row.status === 'success'}
              className="flex-1 h-9 px-3 rounded-md border border-[rgba(55,53,47,0.16)] dark:border-gray-600 bg-transparent text-sm text-[#37352F] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2383E2] disabled:opacity-60"
            />
            <Select
              value={row.roleId}
              onValueChange={value => updateRow(row.key, { roleId: value })}
              disabled={row.status === 'sending' || row.status === 'success'}
            >
              <SelectTrigger className="h-9 w-[160px] bg-transparent border border-[rgba(55,53,47,0.16)] dark:border-gray-600 rounded-md text-sm text-[#37352F] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2383E2]">
                <SelectValue placeholder="Ruolo" />
              </SelectTrigger>
              <SelectContent>
                {roles.map(role => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {row.status === 'sending' && <Loader2 className="w-4 h-4 animate-spin text-[#787774] flex-shrink-0" />}
            {row.status === 'success' && (
              <button
                onClick={() => row.inviteUrl && copyLink(row.key, row.inviteUrl)}
                className="h-9 px-2.5 rounded-md text-xs font-medium flex items-center gap-1.5 flex-shrink-0 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
              >
                {row.copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {row.copied ? 'Copiato' : 'Copia link'}
              </button>
            )}
            {rows.length > 1 && row.status !== 'success' && (
              <button
                onClick={() => removeRow(row.key)}
                className="p-2 rounded-md text-[#787774] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
                title="Rimuovi riga"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {row.status === 'error' && row.errorMessage && (
            <p className="text-xs text-red-600 dark:text-red-400 pl-1">{row.errorMessage}</p>
          )}
        </div>
      ))}

      <button
        onClick={addRow}
        className="flex items-center gap-1.5 text-sm text-[#2383E2] hover:text-[#1a6ab8] transition-colors pt-1"
      >
        <Plus className="w-4 h-4" />
        Aggiungi un altro
      </button>

      {formError && <p className="text-xs text-red-600 dark:text-red-400">{formError}</p>}

      {!hideSubmit && (
        <div className="pt-2 flex justify-end">
          <NotionButton
            variant="primary"
            icon={<UserPlus className="w-4 h-4" />}
            onClick={handleSubmit}
            disabled={submitting || roles.length === 0}
            loading={submitting}
          >
            {submitLabel}
          </NotionButton>
        </div>
      )}
    </div>
  );
});
